import { useQuery } from '@tanstack/react-query';
import { Badge, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchOutreachTracking } from '@/data/sources-salesb';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { formatRelative } from '@/lib/utils';
import type { OutreachEvent, OutreachEventType, OutreachFunnel } from '@/types/salesb';

/* /sales/outreach — Outreach Tracking (W2b). Read-only view over the stored
 * bksesh_sales_lead_events (workspace-scoped).
 *
 * HONESTY + SAFETY INVARIANTS (W2b contract):
 *  - FREE + read-only: reads stored outreach events, NEVER a send, never a
 *    charge. Safe to AUTO-LOAD on mount.
 *  - Source-attributed ("bksesh_sales_lead_events"); funnel counts are real
 *    integers straight from the event table — a stage with no events is a
 *    genuine 0, never invented.
 *  - Honest states: loading / error / empty, via the shared DataState system.
 *  - Uses the existing sales-page table/list style — does NOT import the
 *    SEO-scoped SortableTable (owned by W2a-FE this wave).
 */

const SOURCE_TAG = 'Source: Outreach events (bksesh_sales_lead_events)';

/** The funnel stages, in order, with the funnel key each reads. */
const FUNNEL_STEPS: Array<{ key: keyof OutreachFunnel; label: string }> = [
  { key: 'sent', label: 'Sent' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'replied', label: 'Replied' },
];

const EVENT_TONE: Record<OutreachEventType, Parameters<typeof Badge>[0]['tone']> = {
  SENT: 'neutral',
  OPENED: 'compliance',
  CLICKED: 'ai',
  REPLIED: 'success',
  BOUNCED: 'danger',
};

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** Conversion of a later step against `sent` — honest "—" when there is no base. */
function rateOf(count: number, base: number): string {
  if (base <= 0) return '—';
  return `${((count / base) * 100).toFixed(1)}%`;
}

function FunnelBar({ funnel }: { funnel: OutreachFunnel }) {
  const base = funnel.sent;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {FUNNEL_STEPS.map((step, i) => {
        const count = funnel[step.key];
        return (
          <div
            key={step.key}
            className="rounded-lg border border-border-subtle bg-surface p-4"
          >
            <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{step.label}</div>
            <div className="mt-1 font-mono text-2xl font-semibold text-text-primary" data-tabular>
              {formatInt(count)}
            </div>
            {/* First step is the base — no rate against itself. */}
            <div className="mt-0.5 text-[11px] text-text-tertiary">
              {i === 0 ? 'of window' : `${rateOf(count, base)} of sent`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventRow({ ev }: { ev: OutreachEvent }) {
  return (
    <tr className="border-b border-border-subtle last:border-0 hover:bg-hover">
      <td className="px-3 py-2.5">
        <div className="text-sm font-medium text-text-primary">{ev.business_name}</div>
        <div className="text-xs text-text-tertiary">{ev.lead_id}</div>
      </td>
      <td className="px-3 py-2.5">
        <Badge tone={EVENT_TONE[ev.event_type]}>{ev.event_type}</Badge>
      </td>
      <td className="px-3 py-2.5 text-sm text-text-secondary">{ev.campaign || '—'}</td>
      <td className="px-3 py-2.5 text-sm text-text-secondary">{ev.detail || '—'}</td>
      <td className="px-3 py-2.5 text-right text-xs text-text-tertiary" data-tabular>
        {ev.at ? formatRelative(ev.at) : '—'}
      </td>
    </tr>
  );
}

export function OutreachTrackingPage() {
  const { active } = useActiveDomain();
  // FREE read-only surface — auto-loads. No send, no provider call, no charge.
  // Keyed by the active domain so switching "Viewing" refetches its own events.
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['crm', 'outreach-tracking', active?.projectId ?? 'all'],
    queryFn: () => fetchOutreachTracking(100, active?.projectId),
  });

  const events = data?.events ?? [];
  const source = data?.source ?? 'bksesh_sales_lead_events';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Outreach Tracking"
        subtitle="Sent → opened → clicked → replied across your campaigns, read straight from the stored event record — source-attributed and free to view."
      />

      {isPending ? (
        <LoadingState rows={5} />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <>
          {/* Funnel roll-up — real integers; a zero stage is genuine, not faked. */}
          <FunnelBar funnel={data.funnel} />

          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">{SOURCE_TAG}</Badge>
              <span className="text-xs text-text-tertiary">
                {events.length
                  ? `${formatInt(events.length)} recent ${events.length === 1 ? 'event' : 'events'}`
                  : 'No outreach events recorded yet'}
              </span>
            </div>

            <div className="mt-4">
              {events.length === 0 ? (
                <EmptyState
                  title="No outreach events yet"
                  teach="Events land here as your campaigns send and prospects engage — every send, open, click, reply and bounce is recorded against its lead. Nothing is shown until it actually happens."
                />
              ) : (
                <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] border-collapse">
                      <thead>
                        <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                          <th className="px-3 py-2 font-semibold">Business</th>
                          <th className="px-3 py-2 font-semibold">Event</th>
                          <th className="px-3 py-2 font-semibold">Campaign</th>
                          <th className="px-3 py-2 font-semibold">Detail</th>
                          <th className="px-3 py-2 text-right font-semibold">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((ev, i) => (
                          <EventRow key={`${ev.lead_id}-${ev.at}-${i}`} ev={ev} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                    {formatInt(events.length)} recent{' '}
                    {events.length === 1 ? 'event' : 'events'} · read-only from {source} · this
                    view never sends — it only reflects outreach that already happened.
                  </div>
                </div>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
